/**
 * @fileoverview Shared local-first code search and additional-violation module.
 * @module inspection-renderer/modules/ViolationsModule
 *
 * Responsibilities:
 * - Search host-provided code-set indexes and rank matching entries.
 * - Capture, repair, edit, and remove violation occurrences.
 * - Render a touch-friendly wizard and compact occurrence list.
 *
 * Dependencies:
 * - React, lucide-react, @firesquirrel/core-domain, and renderer provider hooks.
 *
 * Architecture:
 * - Search and photo persistence are injected; violation changes return to the host.
 *
 * Notes:
 * - Hosts retain Dexie reads, local photo storage, and upload behavior.
 */

import { useState, useCallback, useEffect, useRef } from 'react';
import { AlertTriangle, Search, StickyNote, Camera, Trash2, CheckCircle, ChevronRight, ChevronDown, X, ImagePlus, PlusCircle, Wrench } from 'lucide-react';
import { OccurrenceRepairBlock } from '../violations/OccurrenceRepairBlock';
import {
  ADDITIONAL_VIOLATION_FORM_ID,
  type ViolationsModuleConfig,
  type AttachmentRefLike,
  type CodeRef,
} from '@firesquirrel/core-domain';
import {
  applyOccurrenceCorrection,
  clearOccurrenceCorrection,
  removeOccurrenceFromViolation,
} from '@firesquirrel/core-domain';
import type { LoggedViolation, LoggedViolationOccurrence } from '../violations/types';
import {
  useCodeSetSearch,
  useInspectionAuditEvent,
  useThumbnailResolver,
  useThumbnailUrlOwnership,
  useViolationPhotos,
  type InspectorIdentity,
  type ViolationPhotoCaptureResult,
} from '../contracts/rendererContext';
import { rankViolationCodeSetEntries, type ViolationCodeSetIndexEntry } from '../violations/violationSearch';

/**
 * @contract PreviewAttachmentRef
 * Purpose: Carries a host-supplied preview URL on a canonical attachment reference.
 * Fields: Attachment reference fields plus optional previewUri.
 * Rules: previewUri is display-only and is stripped before canonical persistence.
 * Sync: Host owns persistence and upload.
 */
interface PreviewAttachmentRef extends AttachmentRefLike {
  /** Data URL for preview display — NOT persisted to canonical model. */
  previewUri?: string;
}

/**
 * @localFirst
 * Source of Truth: local (Dexie via parent component state)
 * Write Flow: local-first — parent persists to Dexie, background sync wakes
 * Sync Strategy: parent handles sync via onViolationChanges callback
 * Conflict Resolution: last-write-wins at violation level
 */

/**
 * Wizard steps — always shown in this order when enabled:
 *   location  — single-line location descriptor (always shown)
 *   severity  — severity picker (always shown, defaults to 'normal')
 *   notes     — inspector observation note (shown when config.showNotes)
 *   remedy    — remedy / how-to-fix note (shown when config.showNotes)
 *   photos    — photo evidence (shown when config.showPhotos)
 */
type WizardStep = 'location' | 'severity' | 'notes' | 'remedy' | 'photos';

/** State for the in-progress log violation wizard. */
interface WizardState {
  entry: ViolationCodeSetIndexEntry;
  steps: WizardStep[];
  stepIndex: number;
  locationNote: string;
  /** Canonical severity: 'minor' (was 'normal'), 'major' (was 'high'), 'critical' */
  severity: 'minor' | 'major' | 'critical';
  note: string;
  remedyNote: string;
  photos: ViolationPhotoCaptureResult[];
}

/**
 * Generate a unique violation ID.
 * Format: codeId__timestamp__random
 */
function generateViolationId(codeId: string): string {
  return `${codeId}__${Date.now()}__${Math.random().toString(36).slice(2, 7)}`;
}

/**
 * Generate a unique occurrence ID.
 * Format: occ__timestamp__random
 */
function generateOccurrenceId(): string {
  return `occ__${Date.now()}__${Math.random().toString(36).slice(2, 7)}`;
}

/**
 * Build a CodeRef from a ViolationIndexEntry.
 * The canonical LoggedViolation uses CodeRef[] instead of ViolationIndexEntry.
 * CodeRef shape: { code, codeSetId, description? }
 */
function entryToCodeRef(entry: ViolationCodeSetIndexEntry, codeSetId: string): CodeRef {
  return {
    codeSetId,
    code: entry.code,
    description: entry.title,
  };
}

/**
 * Build the ordered list of wizard steps based on config flags.
 * Step order: location → severity → notes → remedy → photos
 *
 * - location: always shown (single-line, always optional)
 * - severity: shown when config.showSeverity is true
 * - notes:    shown when config.showNotes is true
 * - remedy:   shown when config.showRemedy is true
 * - photos:   shown when config.showPhotos is true
 *
 * Mobile: preserve this function — it drives the step sequence.
 */
function buildWizardSteps(config: ViolationsModuleConfig): WizardStep[] {
  const steps: WizardStep[] = ['location'];
  if (config.showSeverity) steps.push('severity');
  if (config.showNotes) steps.push('notes');
  if (config.showRemedy) steps.push('remedy');
  if (config.showPhotos) steps.push('photos');
  return steps;
}

/**
 * @contract ViolationPhotoPreviewProps
 * Purpose: Supplies a photo reference and optional host thumbnail resolver.
 * Fields: Attachment, optional resolver, and accessible image text.
 * Rules: Missing previews render a fallback chip.
 * Sync: Read-only media display.
 */
interface ViolationPhotoPreviewProps {
  attachment: PreviewAttachmentRef;
  resolveThumbnail?: (ref: AttachmentRefLike) => Promise<string | null>;
  alt: string;
}

/**
 * @component ViolationPhotoPreview
 * Purpose: Displays a captured violation photo using local preview or host resolution.
 * Props: ViolationPhotoPreviewProps.
 * Behavior: Resolves attachment thumbnails asynchronously and degrades to a fallback chip.
 * Data Source: local (host-injected resolver).
 * Constraints: Does not mutate attachment data.
 * Performance: Resolves only the displayed attachment.
 */
function ViolationPhotoPreview({ attachment, resolveThumbnail, alt }: ViolationPhotoPreviewProps) {
  const urlOwnership = useThumbnailUrlOwnership();
  const [resolvedUrl, setResolvedUrl] = useState<string | null>(attachment.previewUri ?? null);

  useEffect(() => {
    if (attachment.previewUri) {
      setResolvedUrl(attachment.previewUri);
      return;
    }
    if (!resolveThumbnail) {
      setResolvedUrl(null);
      return;
    }
    let active = true;
    void resolveThumbnail(attachment).then(
      (url) => {
        if (active) setResolvedUrl(url);
        else if (urlOwnership === 'owned' && url?.startsWith('blob:')) {
          // Resolved after this preview was replaced/unmounted — the
          // resolver-URL never reaches state, so revoke it here.
          URL.revokeObjectURL(url);
        }
      },
      () => { if (active) setResolvedUrl(null); },
    );
    return () => { active = false; };
  }, [attachment, resolveThumbnail, urlOwnership]);

  // Revoke owned URLs the resolver minted — NOT attachment.previewUri,
  // which the capture provider owns (a blob preview the photo ref still
  // needs; revoking it would double-free).
  const resolvedByUs = !!resolvedUrl && resolvedUrl !== attachment.previewUri;
  useEffect(() => {
    if (urlOwnership !== 'owned' || !resolvedByUs || !resolvedUrl?.startsWith('blob:')) return;
    return () => URL.revokeObjectURL(resolvedUrl);
  }, [resolvedUrl, resolvedByUs, urlOwnership]);

  return resolvedUrl
    ? <img src={resolvedUrl} alt={alt} className="w-12 h-12 rounded-md object-cover border border-orange-200" />
    : <span className="inline-flex min-h-11 items-center rounded-md border border-gray-200 bg-gray-50 px-2 text-[10px] text-gray-500">Photo preview unavailable</span>;
}

/**
 * @contract ViolationsModuleProps
 * Purpose: Supplies module configuration, current violation state, and host identity.
 * Fields: Config, initial violations, callbacks, optional case/event context, and readOnly.
 * Rules: Mutations are ignored in read-only mode; host providers supply search and photos.
 * Sync: The host persists emitted violation changes through its local-first adapter.
 */
export interface ViolationsModuleProps {
  config: ViolationsModuleConfig;
  initialViolations?: LoggedViolation[];
  /** Compatibility input for the original controlled module contract. */
  violations?: LoggedViolation[];
  /** Called whenever the logged violations list changes — used by preview session data panel. */
  onViolationChanges?: (violations: LoggedViolation[]) => void;
  /** Compatibility callback for the original controlled module contract. */
  onChange?: (violations: LoggedViolation[]) => void;
  inspector?: InspectorIdentity;
  caseId?: string;
  currentEventIndex?: number;
  readOnly?: boolean;
}

/**
 * @component ViolationsModule
 * Purpose: Provides shared code search, violation capture, repair, and removal.
 * Props: ViolationsModuleProps.
 * Behavior: Uses provider-backed local search and photo capture; resets when initial state changes.
 * Data Source: Local (host-provided Dexie records and host-owned persistence callbacks).
 * Constraints: No provider-specific imports; readOnly prevents every mutation.
 * Performance: Displays at most 20 ranked search matches.
 * @errorHandling ViolationsModule
 * Strategy: Use the implementation’s existing catch, return, and propagation behavior.
 * User Impact: Callers can surface operation failures without silently treating them as success.
 * Logging: Retain existing operation logging and avoid sensitive values.
 * @logging ViolationsModule
 * Events: Record the operation outcome and actionable failures.
 * Level: Use the existing operation-appropriate info, warn, or error level.
 * Correlation: Correlate with the case, event, or operation identifier when available.
 */
export function ViolationsModule({
  config,
  initialViolations,
  violations,
  onViolationChanges,
  onChange,
  inspector,
  currentEventIndex = 0,
  readOnly = false,
}: ViolationsModuleProps) {
  const codeSetSearch = useCodeSetSearch();
  const emitInspectionAuditEvent = useInspectionAuditEvent();
  const violationPhotos = useViolationPhotos();
  const thumbnailResolver = useThumbnailResolver();
  const resolveViolationThumbnail = useCallback(async (ref: AttachmentRefLike): Promise<string | null> => {
    if (violationPhotos?.resolveThumbnail) return violationPhotos.resolveThumbnail(ref);
    return thumbnailResolver ? thumbnailResolver(ref._id) : null;
  }, [thumbnailResolver, violationPhotos]);
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<ViolationCodeSetIndexEntry[]>([]);
  const [searching, setSearching] = useState(false);
  const [photoCaptureFailed, setPhotoCaptureFailed] = useState(false);
  const [logged, setLogged] = useState<LoggedViolation[]>(initialViolations ?? violations ?? []);
  const providedViolations = initialViolations ?? violations;
  const onViolationChangesRef = useRef(onViolationChanges ?? onChange);
  onViolationChangesRef.current = onViolationChanges ?? onChange;
  const lastNotifiedViolationsRef = useRef<LoggedViolation[] | null>(null);
  const [draftCode, setDraftCode] = useState('');
  const [draftNotes, setDraftNotes] = useState('');

  /** Active wizard state — null when no wizard is open. */
  const [wizard, setWizard] = useState<WizardState | null>(null);

  /** Set of occurrenceIds whose detail section is expanded. Collapsed by default. */
  const [expandedIds, setExpandedIds] = useState<Set<string>>(new Set());

  /** Which logged violation's note editor is open for post-log editing (by occurrenceId). */
  const [editNoteFor, setEditNoteFor] = useState<string | null>(null);
  const [editNoteText, setEditNoteText] = useState('');

  /**
   * Search the configured code sets for violations matching the query.
   * Results are capped at 20 for display performance.
   */
  const handleSearch = useCallback(async () => {
    if (!query.trim() || !codeSetSearch || readOnly) return;
    setSearching(true);
    try {
      const entries = await codeSetSearch.listIndexEntries(config.codeSetIds);
      setResults(rankViolationCodeSetEntries(entries, query, 20));
    } catch {
      setResults([]);
    } finally {
      setSearching(false);
    }
  }, [codeSetSearch, config.codeSetIds, query, readOnly]);

  /**
   * Open the log-violation wizard for a search result.
   * If the config has no steps (no notes, no photos), commits immediately.
   */
  const openWizard = (entry: ViolationCodeSetIndexEntry) => {
    if (readOnly) return;
    // No duplicate guard — same violation can be logged multiple times (multiple occurrences).
    const steps = buildWizardSteps(config);
    setWizard({ entry, steps, stepIndex: 0, locationNote: '', severity: 'minor' as const, note: '', remedyNote: '', photos: [] });
    setResults([]);
    setQuery('');
  };

  /**
   * Advance the wizard to the next step, or commit if on the last step.
   * canSkip: true when the current step is optional (require* is false).
   */
  const wizardNext = () => {
    if (!wizard || readOnly) return;
    if (wizard.stepIndex < wizard.steps.length - 1) {
      setWizard((w) => w ? { ...w, stepIndex: w.stepIndex + 1 } : null);
    } else {
      commitViolation(wizard);
    }
  };

  /**
   * Commit a violation to the logged list and close the wizard.
   *
   * Canonical structure: LoggedViolation contains occurrences[].
   * If a violation with the same code already exists, add an occurrence to it.
   * Otherwise, create a new violation with one occurrence.
   *
   * `discoveredBy` is stamped from the optional host-provided inspector.
   */
  const commitViolation = (w: WizardState) => {
    if (readOnly) return;
    const codeSetId = w.entry.codeSetId || config.codeSetIds[0] || '';
    if (!codeSetId) console.warn('[ViolationsModule] Selected entry has no code-set ID.');

    const now = new Date().toISOString();
    const inspectorId = inspector?.userId ?? '';

    // Build the new occurrence
    const newOccurrence: LoggedViolationOccurrence = {
      _id: generateOccurrenceId(),
      location: w.locationNote || undefined,
      correctiveActions: w.remedyNote || undefined,
      notes: w.note || undefined,
      status: 'open',
      discoveredInEventIndex: currentEventIndex,
      discoveredAt: now,
      discoveredBy: inspectorId,
      attachments: w.photos.map((photo): PreviewAttachmentRef => ({
        _id: photo.attachmentId ?? photo.localId ?? `temp__${Date.now()}__${Math.random().toString(36).slice(2, 7)}`,
        parentType: 'occurrence',
        parentId: '', // Will be set when persisting
        kind: 'photo',
        blobName: photo.attachmentId ?? '',
        previewUri: photo.previewUrl,
      })),
    };

    // Build the code key for grouping
    const codeKey = `${codeSetId}::${w.entry.code}`;
    const existingViolation = logged.find((v) => {
      const vCodeKey = v.codes[0] ? `${v.codes[0].codeSetId}::${v.codes[0].code}` : '';
      return vCodeKey === codeKey;
    });
    const violationId = existingViolation?._id ?? generateViolationId(w.entry._id);
    const occurrenceIndex = existingViolation?.occurrences.length ?? 0;

    setLogged((prev) => {
      // Find existing violation with same code
      const existingIdx = prev.findIndex((v) => {
        const vCodeKey = v.codes[0] ? `${v.codes[0].codeSetId}::${v.codes[0].code}` : '';
        return vCodeKey === codeKey;
      });

      if (existingIdx >= 0) {
        // Add occurrence to existing violation
        const existing = prev[existingIdx];
        const updated: LoggedViolation = {
          ...existing,
          occurrences: [...existing.occurrences, newOccurrence],
        };
        const newList = [...prev];
        newList[existingIdx] = updated;
        return newList;
      } else {
        // Create new violation with one occurrence
        const newViolation: LoggedViolation = {
          _id: violationId,
          fieldId: null, // Additional violations have no field binding
          formId: ADDITIONAL_VIOLATION_FORM_ID,
          codes: [entryToCodeRef(w.entry, codeSetId)],
          severity: w.severity,
          status: 'open',
          discoveredInEventIndex: currentEventIndex,
          source: 'additional',
          occurrences: [newOccurrence],
        };
        return [...prev, newViolation];
      }
    });
    emitInspectionAuditEvent({
      kind: 'violation_occurrence_logged',
      fieldId: ADDITIONAL_VIOLATION_FORM_ID,
      violationId,
      violationCode: w.entry.code,
      occurrenceId: newOccurrence._id,
      occurrenceIndex,
      location: newOccurrence.location,
    }, readOnly);

    setWizard(null);
    setResults([]);
    setQuery('');
  };

  const logAdHocViolation = () => {
    if (readOnly || !draftCode.trim() || (config.requireNotes && !draftNotes.trim())) return;
    const now = new Date().toISOString();
    const id = generateViolationId(draftCode.trim());
    const occurrenceId = generateOccurrenceId();
    const violation: LoggedViolation = {
      _id: id,
      fieldId: null,
      formId: ADDITIONAL_VIOLATION_FORM_ID,
      codes: [{ code: draftCode.trim(), codeSetId: config.codeSetIds[0] ?? '' }],
      severity: 'major',
      status: 'open',
      discoveredInEventIndex: currentEventIndex,
      source: 'additional',
      occurrences: [{
        _id: occurrenceId,
        status: 'open',
        discoveredInEventIndex: currentEventIndex,
        discoveredAt: now,
        discoveredBy: inspector?.userId ?? '',
        notes: draftNotes.trim() || undefined,
        attachments: [],
      }],
    };
    setLogged((current) => [...current, violation]);
    emitInspectionAuditEvent({
      kind: 'violation_occurrence_logged',
      fieldId: ADDITIONAL_VIOLATION_FORM_ID,
      violationId: id,
      violationCode: draftCode.trim(),
      occurrenceId,
      occurrenceIndex: 0,
      summaryContext: 'Additional violations',
    }, readOnly);
    setDraftCode('');
    setDraftNotes('');
  };

  /** Cancel the wizard without logging. */
  const cancelWizard = () => { if (!readOnly) setWizard(null); };

  /**
   * Remove a logged violation occurrence by its occurrenceId.
   * If the violation has only one occurrence, remove the entire violation.
   * If it has multiple occurrences, remove just that occurrence.
   */
  const removeOccurrence = (violationId: string, occurrenceId: string) => {
    if (readOnly) return;
    const violation = logged.find((item) => item._id === violationId);
    const occurrenceIndex = violation?.occurrences.findIndex((item) => item._id === occurrenceId) ?? -1;
    const occurrence = violation?.occurrences[occurrenceIndex];
    if (!violation || !occurrence) return;
    setLogged((prev) => {
      const violationIdx = prev.findIndex((v) => v._id === violationId);
      if (violationIdx < 0) return prev;

      const violation = prev[violationIdx];
      const { remainingOccurrences, dropViolation } = removeOccurrenceFromViolation(
        violation.occurrences,
        occurrenceId,
        violation.source,
      );
      if (dropViolation) return prev.filter((v) => v._id !== violationId);
      const updated: LoggedViolation = { ...violation, occurrences: remainingOccurrences };
      const newList = [...prev];
      newList[violationIdx] = updated;
      return newList;
    });
    setExpandedIds((prev) => { const s = new Set(prev); s.delete(occurrenceId); return s; });
    emitInspectionAuditEvent({
      kind: 'violation_occurrence_removed',
      fieldId: violation.fieldId ?? ADDITIONAL_VIOLATION_FORM_ID,
      violationId: violation._id,
      violationCode: violation.codes[0]?.code,
      occurrenceId,
      occurrenceIndex,
      location: occurrence.location,
    }, readOnly);
  };

  /**
   * Save an edited note on an already-logged occurrence.
   * Updates the occurrence's `notes` field.
   */
  const saveEditedNote = (violationId: string, occurrenceId: string) => {
    if (readOnly) return;
    setLogged((prev) =>
      prev.map((v) => {
        if (v._id !== violationId) return v;
        return {
          ...v,
          occurrences: v.occurrences.map((o) =>
            o._id === occurrenceId ? { ...o, notes: editNoteText } : o
          ),
        };
      }),
    );
    setEditNoteFor(null);
    setEditNoteText('');
  };

  /**
   * Mark an occurrence as repaired.
   * Delegates the stamp write to the shared `applyOccurrenceCorrection`
   * (@firesquirrel/core-domain) so the repaired shape (status/correctionStatus/
   * fixedAt/fixedBy/fixedNotes) remains canonical.
   */
  const markOccurrenceRepaired = (violationId: string, occurrenceId: string, repairNotes: string) => {
    if (readOnly) return;
    const now = new Date().toISOString();
    const inspectorId = inspector?.userId ?? '';

    setLogged((prev) =>
      prev.map((v) => {
        if (v._id !== violationId) return v;
        const updatedOccurrences = v.occurrences.map((o) =>
          o._id === occurrenceId
            ? applyOccurrenceCorrection(o, {
                correctionType: 'repaired',
                fixedBy: inspectorId,
                fixedAt: now,
                fixedInEventIndex: currentEventIndex,
                fixedNotes: repairNotes,
              })
            : o
        );
        // Update violation status if all occurrences are resolved/closed
        const allResolved = updatedOccurrences.every(
          (o) => o.status === 'resolved' || o.status === 'closed'
        );
        return {
          ...v,
          occurrences: updatedOccurrences,
          status: allResolved ? 'resolved' : v.status,
        };
      }),
    );
  };

  /**
   * Undo a repair on an occurrence.
   * Delegates to the shared `clearOccurrenceCorrection`
   * (@firesquirrel/core-domain) which reopens the occurrence and clears the
   * stamp in a consistent way.
   */
  const undoOccurrenceRepair = (violationId: string, occurrenceId: string) => {
    if (readOnly) return;
    setLogged((prev) =>
      prev.map((v) => {
        if (v._id !== violationId) return v;
        const updatedOccurrences = v.occurrences.map((o) =>
          o._id === occurrenceId ? clearOccurrenceCorrection(o) : o
        );
        // Update violation status if any occurrence is open
        const anyOpen = updatedOccurrences.some((o) => o.status === 'open');
        return {
          ...v,
          occurrences: updatedOccurrences,
          status: anyOpen ? 'open' : v.status,
        };
      }),
    );
  };

  useEffect(() => {
    if (providedViolations) setLogged(providedViolations);
  }, [providedViolations]);

  // Notify parent whenever the logged list changes
  useEffect(() => {
    if (
      lastNotifiedViolationsRef.current &&
      JSON.stringify(lastNotifiedViolationsRef.current) === JSON.stringify(logged)
    ) {
      return;
    }
    lastNotifiedViolationsRef.current = logged;
    onViolationChangesRef.current?.(logged);
  }, [logged]);

  const toggleExpanded = (occurrenceId: string) =>
    setExpandedIds((prev) => {
      const s = new Set(prev);
      s.has(occurrenceId) ? s.delete(occurrenceId) : s.add(occurrenceId);
      return s;
    });

  // Wizard: can the inspector proceed / is the current step satisfied?
  const wizardStepSatisfied = (): boolean => {
    if (!wizard || readOnly) return false;
    const step = wizard.steps[wizard.stepIndex];
    if (step === 'location') return true; // always optional
    if (step === 'severity') return true; // always has a default
    if (step === 'notes') return config.requireNotes ? wizard.note.trim().length > 0 : true;
    if (step === 'remedy') return true; // remedy note is always optional
    if (step === 'photos') return config.requirePhotos ? !!violationPhotos && wizard.photos.length > 0 : true;
    return true;
  };

  // Wizard: is the current step skippable (optional and inspector hasn't filled it)
  const wizardStepSkippable = (): boolean => {
    if (!wizard) return false;
    const step = wizard.steps[wizard.stepIndex];
    if (step === 'location') return true;
    if (step === 'severity') return false; // always has a value, no skip needed
    if (step === 'notes') return !config.requireNotes;
    if (step === 'remedy') return true;
    if (step === 'photos') return !config.requirePhotos;
    return false;
  };

  const isLastStep = wizard ? wizard.stepIndex === wizard.steps.length - 1 : false;

  return (
    <fieldset disabled={readOnly} data-module-type="violations" data-read-only={readOnly ? 'true' : 'false'} data-allow-ai-search={config.allowAiSearch ? 'true' : 'false'} className="m-0 min-w-0 border-0 p-0 [&_button]:min-h-11 [&_button]:min-w-11">
    <div className="p-4 space-y-4">

      {/* Module header */}
      <div className="flex items-center gap-2">
        <AlertTriangle size={16} className="text-orange-600" />
        <span className="font-semibold text-gray-800 text-sm">Additional Violations</span>
        {logged.length > 0 && (
          <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-orange-100 text-orange-700 font-medium">
            {logged.length}
          </span>
        )}
      </div>

      {/* ── Log Violation Wizard ── */}
      {wizard && (
        <div className="border-2 border-orange-300 rounded-xl bg-orange-50 overflow-hidden">

          {/* Wizard header */}
          <div className="px-4 py-3 bg-orange-600 flex items-start justify-between gap-3">
            <div className="min-w-0">
              <div className="flex items-center gap-2 mb-0.5">
                <span className="text-[10px] font-bold text-orange-200 uppercase tracking-wide">
                  Logging Violation
                </span>
                {wizard.steps.length > 1 && (
                  <span className="text-[10px] text-orange-200">
                    Step {wizard.stepIndex + 1} of {wizard.steps.length}
                  </span>
                )}
              </div>
              <div className="text-white font-semibold text-sm truncate">{wizard.entry.title}</div>
              <div className="text-orange-200 text-[10px] font-mono">{wizard.entry.code}</div>
            </div>
            <button
              onClick={cancelWizard}
              className="p-1 text-orange-200 hover:text-white transition-colors shrink-0 mt-0.5"
              title="Cancel"
            >
              <X size={14} />
            </button>
          </div>

          {/* Step progress dots */}
          {wizard.steps.length > 1 && (
            <div className="flex items-center gap-1.5 px-4 py-2 bg-orange-100 border-b border-orange-200">
              {wizard.steps.map((step, i) => (
                <div key={step} className="flex items-center gap-1.5">
                  <div className={`w-2 h-2 rounded-full transition-colors ${
                    i < wizard.stepIndex ? 'bg-orange-500' :
                    i === wizard.stepIndex ? 'bg-orange-600' : 'bg-orange-200'
                  }`} />
                  <span className={`text-[9px] font-medium uppercase tracking-wide ${
                    i === wizard.stepIndex ? 'text-orange-700' : 'text-orange-400'
                  }`}>
                    {step === 'location' ? 'Location'
                      : step === 'severity' ? 'Severity'
                      : step === 'notes' ? (config.requireNotes ? 'Notes *' : 'Notes')
                      : step === 'remedy' ? 'Remedy'
                      : (config.requirePhotos ? 'Photo *' : 'Photo')
                    }
                  </span>
                  {i < wizard.steps.length - 1 && <ChevronRight size={10} className="text-orange-300" />}
                </div>
              ))}
            </div>
          )}

          {/* Violation detail snippet */}
          {wizard.entry.contentSnippet && (
            <div className="px-4 pt-3 pb-1">
              <p className="text-[10px] text-gray-500 italic leading-relaxed">{wizard.entry.contentSnippet}</p>
            </div>
          )}

          {/* ── Step: Location ── */}
          {wizard.steps[wizard.stepIndex] === 'location' && (
            <div className="px-4 py-3 space-y-2">
              <label className="block text-xs font-semibold text-gray-700">
                <span className="flex items-center gap-1">
                  <span className="text-base leading-none">📍</span>
                  Location
                  <span className="text-[10px] text-gray-400 font-normal ml-1">(optional — e.g. "back office", "front door")</span>
                </span>
              </label>
              <input
                type="text"
                value={wizard.locationNote}
                onChange={(e) => setWizard((w) => w ? { ...w, locationNote: e.target.value } : null)}
                placeholder="Where on the property was this violation observed?"
                autoFocus
                className="w-full px-3 py-2 text-xs border border-orange-300 rounded-lg bg-white focus:outline-hidden focus:ring-2 focus:ring-orange-400 placeholder-gray-400"
              />
            </div>
          )}

          {/* ── Step: Severity ── */}
          {wizard.steps[wizard.stepIndex] === 'severity' && (
            <div className="px-4 py-3 space-y-2">
              <label className="block text-xs font-semibold text-gray-700 mb-1">
                Severity Assessment
              </label>
              <div className="flex gap-2">
                {/* Canonical severity values: 'minor' (was 'normal'), 'major' (was 'high'), 'critical' */}
                {(['minor', 'major', 'critical'] as const).map((level) => {
                  const active = wizard.severity === level;
                  const colors = level === 'critical'
                    ? active ? 'bg-red-600 text-white border-red-600' : 'border-red-300 text-red-600 hover:bg-red-50'
                    : level === 'major'
                    ? active ? 'bg-amber-500 text-white border-amber-500' : 'border-amber-300 text-amber-600 hover:bg-amber-50'
                    : active ? 'bg-orange-600 text-white border-orange-600' : 'border-orange-300 text-orange-600 hover:bg-orange-50';
                  const label = level === 'minor' ? 'Minor' : level === 'major' ? 'Major' : 'Critical';
                  const desc = level === 'minor' ? 'Standard violation'
                    : level === 'major' ? 'Elevated — prioritize correction'
                    : 'Immediate action required';
                  return (
                    <button
                      key={level}
                      onClick={() => setWizard((w) => w ? { ...w, severity: level } : null)}
                      className={`flex-1 flex flex-col items-center py-3 px-2 rounded-xl border-2 transition-colors ${colors}`}
                    >
                      <span className="text-xs font-bold">{label}</span>
                      <span className={`text-[9px] mt-0.5 ${active ? 'opacity-80' : 'opacity-60'}`}>{desc}</span>
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {/* ── Step: Notes ── */}
          {wizard.steps[wizard.stepIndex] === 'notes' && (
            <div className="px-4 py-3 space-y-2">
              <label className="block text-xs font-semibold text-gray-700">
                {config.requireNotes ? (
                  <span className="flex items-center gap-1">
                    <StickyNote size={12} className="text-orange-600" />
                    Corrective Action Note
                    <span className="text-red-500 ml-0.5">*</span>
                    <span className="text-[10px] text-red-500 font-normal ml-1">(required)</span>
                  </span>
                ) : (
                  <span className="flex items-center gap-1">
                    <StickyNote size={12} className="text-gray-400" />
                    Corrective Action Note
                    <span className="text-[10px] text-gray-400 font-normal ml-1">(optional)</span>
                  </span>
                )}
              </label>
              <textarea
                value={wizard.note}
                onChange={(e) => setWizard((w) => w ? { ...w, note: e.target.value } : null)}
                placeholder={config.requireNotes
                  ? 'Describe the corrective action required to resolve this violation…'
                  : 'Optionally describe the corrective action or add inspector notes…'
                }
                rows={3}
                autoFocus
                className="w-full px-3 py-2 text-xs border border-orange-300 rounded-lg bg-white focus:outline-hidden focus:ring-2 focus:ring-orange-400 resize-none placeholder-gray-400"
              />
            </div>
          )}

          {/* ── Step: Remedy ── */}
          {wizard.steps[wizard.stepIndex] === 'remedy' && (
            <div className="px-4 py-3 space-y-2">
              <label className="block text-xs font-semibold text-gray-700">
                <span className="flex items-center gap-1">
                  <StickyNote size={12} className="text-gray-400" />
                  Remedy / Corrective Action
                  <span className="text-[10px] text-gray-400 font-normal ml-1">(optional — how to fix this violation)</span>
                </span>
              </label>
              <textarea
                value={wizard.remedyNote}
                onChange={(e) => setWizard((w) => w ? { ...w, remedyNote: e.target.value } : null)}
                placeholder="Describe the corrective action or mitigation steps required to resolve this violation…"
                rows={3}
                autoFocus
                className="w-full px-3 py-2 text-xs border border-orange-300 rounded-lg bg-white focus:outline-hidden focus:ring-2 focus:ring-orange-400 resize-none placeholder-gray-400"
              />
            </div>
          )}

          {/* ── Step: Photos ── */}
          {wizard.steps[wizard.stepIndex] === 'photos' && (
            <div className="px-4 py-3 space-y-2">
              <label className="block text-xs font-semibold text-gray-700">
                {config.requirePhotos ? (
                  <span className="flex items-center gap-1">
                    <Camera size={12} className="text-orange-600" />
                    Photo Evidence
                    <span className="text-red-500 ml-0.5">*</span>
                    <span className="text-[10px] text-red-500 font-normal ml-1">(required)</span>
                  </span>
                ) : (
                  <span className="flex items-center gap-1">
                    <Camera size={12} className="text-gray-400" />
                    Photo Evidence
                    <span className="text-[10px] text-gray-400 font-normal ml-1">(optional)</span>
                  </span>
                )}
              </label>

              {/* Captured photos */}
              {wizard.photos.length > 0 && (
                <div className="flex flex-wrap gap-2">
                  {wizard.photos.map((photo, i) => (
                    <div key={i} className="relative w-16 h-16 rounded-lg overflow-hidden border border-orange-300">
                      {photo.previewUrl
                        ? <img src={photo.previewUrl} alt={`Photo ${i + 1}`} className="w-full h-full object-cover" />
                        : <span className="flex h-full items-center justify-center text-[9px] text-gray-500">Photo saved</span>}
                      {!readOnly && (
                        <button
                          type="button"
                          onClick={() => setWizard((w) => w ? { ...w, photos: w.photos.filter((_, j) => j !== i) } : null)}
                          className="absolute right-0 top-0 flex min-h-11 min-w-11 items-center justify-center rounded-full bg-red-500 text-white"
                          title="Remove photo"
                        >
                          <X size={12} />
                        </button>
                      )}
                    </div>
                  ))}
                </div>
              )}

              {violationPhotos && (
                <label className="flex min-h-11 items-center gap-2 rounded-xl border-2 border-dashed border-orange-300 bg-white px-3 py-2.5 transition-colors hover:bg-orange-50">
                  <ImagePlus size={16} className="shrink-0 text-orange-400" />
                  <span className="text-xs font-medium text-orange-600">
                    {wizard.photos.length === 0 ? 'Tap to attach photo' : 'Attach another photo'}
                  </span>
                  <input
                    type="file"
                    accept="image/*"
                    multiple
                    capture="environment"
                    className="sr-only"
                    onChange={async (e) => {
                      const files = Array.from(e.target.files || []);
                      for (const file of files) {
                        try {
                          const photo = await violationPhotos.capture(file);
                          setWizard((w) => w ? { ...w, photos: [...w.photos, photo] } : null);
                        } catch {
                          setPhotoCaptureFailed(true);
                        }
                      }
                      e.target.value = '';
                    }}
                  />
                </label>
              )}
              {photoCaptureFailed && <p role="alert" className="text-[10px] text-red-600">Could not capture photo. Try again.</p>}
              {!violationPhotos && (
                <p role="status" className="text-[10px] text-gray-500">Photo capture unavailable</p>
              )}

              {config.requirePhotos && wizard.photos.length === 0 && (
                <p className="text-[10px] text-red-500 font-medium">At least one photo is required before logging.</p>
              )}
            </div>
          )}

          {/* Wizard footer actions */}
          <div className="px-4 py-3 bg-white border-t border-orange-200 flex items-center justify-between gap-3">
            <button
              onClick={cancelWizard}
              className="px-3 py-1.5 text-xs text-gray-600 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
            >
              Cancel
            </button>

            <div className="flex items-center gap-2">
              {/* Skip — shown only when the current step is optional */}
              {wizardStepSkippable() && (
                <button
                  onClick={wizardNext}
                  className="px-3 py-1.5 text-xs text-gray-500 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
                >
                  Skip
                </button>
              )}

              <button
                onClick={wizardNext}
                disabled={!wizardStepSatisfied()}
                className="flex items-center gap-1.5 px-4 py-1.5 text-xs font-semibold text-white rounded-lg disabled:opacity-40 transition-colors bg-orange-600 hover:bg-orange-700"
              >
                {isLastStep ? (
                  <><CheckCircle size={12} /> Log Violation</>
                ) : (
                  <>Next <ChevronRight size={12} /></>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {!codeSetSearch && <p role="status" className="rounded-lg bg-amber-50 px-3 py-2 text-xs text-amber-800">Code search unavailable</p>}

      {/* Search bar — hidden while wizard is open */}
      {!wizard && (
        <div className="flex gap-2">
          <div className="flex-1 flex items-center gap-2 px-3 py-2 rounded-xl border border-gray-300 bg-white focus-within:ring-2 focus-within:ring-orange-300 focus-within:border-orange-400 transition-all">
            <Search size={14} className="text-gray-400 shrink-0" />
            <input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
              disabled={!codeSetSearch}
              placeholder="Search violations by keyword…"
              className="flex-1 text-sm bg-transparent outline-hidden text-gray-700 placeholder-gray-400"
            />
          </div>
          <button
            onClick={handleSearch}
            disabled={!codeSetSearch || !query.trim() || searching}
            data-action={config.allowAiSearch ? 'search' : undefined}
            className="px-4 py-2 text-sm font-medium text-white rounded-xl disabled:opacity-40 transition-colors"
            style={{ backgroundColor: '#ea580c' }}
          >
            {searching ? '…' : 'Search'}
          </button>
        </div>
      )}

      {!readOnly && (
        <div className="grid gap-2 border-t border-gray-200 pt-3">
          <label className="grid gap-1 text-xs font-medium text-gray-700">
            Violation code
            <input
              type="text"
              value={draftCode}
              onChange={(event) => setDraftCode(event.target.value)}
              placeholder="Code (e.g. 906.1)"
              data-action="draft-code"
              className="min-h-11 rounded-lg border border-gray-300 px-3 text-sm"
            />
          </label>
          {config.showNotes && (
            <label className="grid gap-1 text-xs font-medium text-gray-700">
              Notes
              <textarea
                value={draftNotes}
                onChange={(event) => setDraftNotes(event.target.value)}
                placeholder="Notes"
                aria-invalid={config.requireNotes && !draftNotes.trim()}
                data-action="draft-notes"
                className="min-h-11 rounded-lg border border-gray-300 p-3 text-sm"
              />
            </label>
          )}
          <button
            type="button"
            onClick={logAdHocViolation}
            disabled={!draftCode.trim() || (config.requireNotes && !draftNotes.trim())}
            data-action="log-ad-hoc"
            className="min-h-11 self-start rounded-lg bg-orange-600 px-4 py-2 text-sm font-semibold text-white disabled:opacity-40"
          >
            Log Violation
          </button>
        </div>
      )}

      {/* Search results */}
      {!wizard && results.length > 0 && (
        <div className="border border-gray-200 rounded-xl overflow-hidden">
          <div className="px-3 py-2 bg-gray-50 border-b text-[10px] font-semibold text-gray-500 uppercase tracking-wide">
            {results.length} result{results.length !== 1 ? 's' : ''} — tap to log
          </div>
          <div className="divide-y divide-gray-100 max-h-64 overflow-y-auto">
            {results.map((r) => {
              // Count total occurrences across all violations matching this code
              const matchingViolation = logged.find((v) => v.codes[0]?.code === r.code);
              const occurrenceCount = matchingViolation?.occurrences.length ?? 0;
              return (
                <button
                  key={r._id}
                  onClick={() => openWizard(r)}
                  className="w-full text-left px-3 py-2.5 transition-colors hover:bg-orange-50"
                >
                  <div className="flex items-start gap-3">
                    <span className="text-[10px] font-bold text-orange-600 shrink-0 mt-0.5 w-36 truncate" title={r.code}>{r.code}</span>
                    <div className="min-w-0 flex-1">
                      <div className="text-xs font-medium text-gray-800 truncate">{r.title}</div>
                      <div className="text-[10px] text-gray-400 line-clamp-2">{r.contentSnippet}</div>
                    </div>
                    <div className="flex items-center gap-1.5 shrink-0 mt-0.5">
                      {occurrenceCount > 0 && (
                        <span className="text-[9px] px-1.5 py-0.5 rounded-full bg-green-100 text-green-700 font-medium">
                          {occurrenceCount}×
                        </span>
                      )}
                      <ChevronRight size={12} className="text-orange-400" />
                    </div>
                  </div>
                </button>
              );
            })}
          </div>
        </div>
      )}

      {/* Empty state */}
      {!wizard && logged.length === 0 && results.length === 0 && (
        <div className="text-center py-8 text-gray-400 text-sm border-2 border-dashed border-gray-200 rounded-xl">
          <AlertTriangle size={24} className="mx-auto mb-2 opacity-30" />
          Search and log violations not covered by the checklist
        </div>
      )}

      {/* Logged violations list — now nested: violation → occurrences[] */}
      <div className="space-y-2">
        {logged.map((violation) => {
          const { _id: violationId, codes, severity, occurrences } = violation;
          const codeRef = codes[0];
          const code = codeRef?.code ?? '';
          const title = codeRef?.description ?? '';
          const occurrenceCount = occurrences.length;

          // Severity badge using canonical values
          const severityBadge = severity === 'critical'
            ? <span className="text-[9px] font-bold px-1.5 py-0.5 rounded-full bg-red-100 text-red-700">Critical</span>
            : severity === 'major'
            ? <span className="text-[9px] font-bold px-1.5 py-0.5 rounded-full bg-amber-100 text-amber-700">Major</span>
            : null; // minor = no badge (it's the default)

          return (
            <div key={violationId} className="rounded-xl border bg-orange-50 overflow-hidden border-orange-200">

              {/* ── Violation header — code + severity + occurrence count ── */}
              <div className="flex items-center gap-2 px-3 py-2 bg-orange-100 border-b border-orange-200">
                <CheckCircle size={13} className="text-green-500 shrink-0" />
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-1.5 flex-wrap">
                    <span className="text-[10px] font-bold text-orange-600">{code}</span>
                    <span className="text-xs font-medium text-gray-800 truncate">{title}</span>
                    {occurrenceCount > 1 && (
                      <span className="text-[9px] px-1.5 py-0.5 rounded-full bg-orange-200 text-orange-700 font-semibold shrink-0">
                        {occurrenceCount} occurrences
                      </span>
                    )}
                    {config.showSeverity && severityBadge}
                  </div>
                </div>
                {/* Add another occurrence */}
                <button
                  onClick={() => {
                    // Reconstruct minimal ViolationIndexEntry from CodeRef for wizard
                    // Only the fields used by openWizard/commitViolation are needed
                    const entry = {
                      _id: codeRef?.codeSetId ? `${codeRef.codeSetId}::${code}` : code,
                      code,
                      title: title,
                      contentSnippet: '',
                      codeSetId: codeRef?.codeSetId ?? '',
                      codeReference: code,
                      source: 'custom' as const,
                      isActive: true,
                      createdAt: new Date().toISOString(),
                      updatedAt: new Date().toISOString(),
                    } satisfies ViolationCodeSetIndexEntry;
                    openWizard(entry);
                  }}
                  className="flex min-h-11 min-w-11 items-center justify-center text-orange-400 transition-colors hover:text-orange-600"
                  title="Log another occurrence of this violation"
                >
                  <PlusCircle size={13} />
                </button>
              </div>

              {/* ── Occurrences list ── */}
              <div className="divide-y divide-orange-100">
                {occurrences.map((occ, occIdx) => {
                  const { _id: occurrenceId, location, notes, correctiveActions, attachments } = occ;
                  const isExpanded = expandedIds.has(occurrenceId);
                  const photos = attachments as PreviewAttachmentRef[];
                  const hasDetail = notes || correctiveActions || photos.length > 0;
                  const missingNote = config.requireNotes && !notes && config.showNotes;
                  const missingPhoto = config.requirePhotos && photos.length === 0 && config.showPhotos;
                  const hasWarning = missingNote || missingPhoto;

                  return (
                    <div key={occurrenceId} className={`${hasWarning ? 'bg-red-50' : 'bg-white'}`}>
                      {/* Occurrence summary row */}
                      <div className="flex items-center gap-2 px-3 py-2">
                        <span className="text-[9px] text-gray-400 font-mono w-6">#{occIdx + 1}</span>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-1.5 flex-wrap">
                            {/* Repair block — status badge + repair/undo button visible without expanding */}
                            <OccurrenceRepairBlock
                              occurrence={occ}
                              violationCode={code}
                              violationDescription={title}
                              fieldId={violation.fieldId ?? ADDITIONAL_VIOLATION_FORM_ID}
                              violationId={violationId}
                              occurrenceIndex={occIdx}
                              eventIndex={currentEventIndex}
                              onMarkRepaired={(occId, repairData) => markOccurrenceRepaired(violationId, occId, repairData.repairNotes)}
                              onReopen={(occId) => undoOccurrenceRepair(violationId, occId)}
                              readOnly={readOnly}
                              compact={true}
                            />
                            {location && (
                              <span className="text-[10px] text-gray-600">📍 {location}</span>
                            )}
                            {!location && <span className="text-[10px] text-gray-400 italic">No location</span>}
                          </div>
                          {hasWarning && (
                            <div className="flex gap-2 mt-0.5">
                              {missingNote && <span className="text-[9px] text-red-500 font-medium">Note required</span>}
                              {missingPhoto && <span className="text-[9px] text-red-500 font-medium">Photo required</span>}
                            </div>
                          )}
                        </div>

                        {/* Occurrence actions */}
                        <div className="flex items-center gap-0.5 shrink-0">
                          {(hasDetail || config.showNotes || config.showRemedy) && (
                            <button
                              onClick={() => toggleExpanded(occurrenceId)}
                              className="p-1 text-gray-400 hover:text-orange-600 transition-colors"
                              aria-label={isExpanded ? 'Collapse details' : 'Expand details'}
                              title={isExpanded ? 'Collapse details' : 'Expand details'}
                            >
                              {isExpanded ? <ChevronDown size={13} /> : <ChevronRight size={13} />}
                            </button>
                          )}
                          <button
                            onClick={() => removeOccurrence(violationId, occurrenceId)}
                            className="p-1 text-gray-300 hover:text-red-400 transition-colors"
                            title="Remove this occurrence"
                          >
                            <Trash2 size={12} />
                          </button>
                        </div>
                      </div>

                      {/* Occurrence detail section */}
                      {isExpanded && (
                        <div className="border-t border-orange-100 bg-gray-50 px-3 py-2.5 space-y-2.5 ml-6">

                          {/* Inspector note */}
                          {config.showNotes && (
                            <div>
                              <div className="text-[9px] font-semibold text-gray-500 uppercase tracking-wide mb-0.5 flex items-center justify-between">
                                <span>Inspector Note {config.requireNotes && <span className="text-red-400">*</span>}</span>
                                {config.allowCorrectiveAction && editNoteFor !== occurrenceId && (
                                  <button
                                    onClick={() => { setEditNoteFor(occurrenceId); setEditNoteText(notes ?? ''); }}
                                    className="text-orange-500 hover:text-orange-700 text-[9px] font-medium"
                                  >Edit</button>
                                )}
                              </div>
                              {editNoteFor === occurrenceId ? (
                                <div className="space-y-1">
                                  <textarea
                                    value={editNoteText}
                                    onChange={(e) => setEditNoteText(e.target.value)}
                                    placeholder={config.requireNotes ? 'Note required…' : 'Add note…'}
                                    rows={2}
                                    className="w-full px-2.5 py-1.5 text-xs border border-gray-200 rounded-lg bg-white focus:outline-hidden focus:ring-1 focus:ring-orange-300 resize-none"
                                  />
                                  <div className="flex gap-1">
                                    <button onClick={() => saveEditedNote(violationId, occurrenceId)} className="px-2.5 py-1 text-xs font-medium text-white rounded-lg bg-orange-600 hover:bg-orange-700">Save</button>
                                    <button onClick={() => setEditNoteFor(null)} className="px-2.5 py-1 text-xs text-gray-600 border border-gray-200 rounded-lg">Cancel</button>
                                  </div>
                                </div>
                              ) : notes ? (
                                <p className="text-[10px] text-gray-600 italic">"{notes}"</p>
                              ) : (
                                <p className="text-[10px] text-gray-400">No note added.</p>
                              )}
                            </div>
                          )}

                          {/* Corrective actions (was remedyNote) */}
                          {config.showRemedy && (
                            <div>
                              <div className="text-[9px] font-semibold text-gray-500 uppercase tracking-wide mb-0.5">Corrective Actions</div>
                              {correctiveActions
                                ? <p className="text-[10px] text-gray-600 italic">"{correctiveActions}"</p>
                                : <p className="text-[10px] text-gray-400">No corrective actions added.</p>}
                            </div>
                          )}

                          {/* Photos */}
                          {config.showPhotos && (
                            <div>
                              <div className="text-[9px] font-semibold text-gray-500 uppercase tracking-wide mb-1">
                                Photos {config.requirePhotos && <span className="text-red-400">*</span>}
                                <span className="ml-1 font-normal text-gray-400">({photos.length})</span>
                              </div>
                              {photos.length > 0 ? (
                                <div className="flex flex-wrap gap-1.5">
                                  {photos.map((attachment, i) => (
                                    <ViolationPhotoPreview
                                      key={attachment._id ?? i}
                                      attachment={attachment}
                                      resolveThumbnail={resolveViolationThumbnail}
                                      alt={`Photo ${i + 1}`}
                                    />
                                  ))}
                                </div>
                              ) : (
                                <p className="text-[10px] text-gray-400">No photos attached.</p>
                              )}
                            </div>
                          )}

                          {/* Repair status and actions */}
                          <div className="pt-2 border-t border-gray-200">
                            <div className="text-[9px] font-semibold text-gray-500 uppercase tracking-wide mb-1.5">
                              Repair Status
                            </div>
                            <OccurrenceRepairBlock
                              occurrence={occ}
                              violationCode={code}
                              violationDescription={title}
                              fieldId={violation.fieldId ?? ADDITIONAL_VIOLATION_FORM_ID}
                              violationId={violationId}
                              occurrenceIndex={occIdx}
                              eventIndex={currentEventIndex}
                              onMarkRepaired={(occId, repairData) => markOccurrenceRepaired(violationId, occId, repairData.repairNotes)}
                              onReopen={(occId) => undoOccurrenceRepair(violationId, occId)}
                              readOnly={readOnly}
                              compact={true}
                            />
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>
    </div>
    </fieldset>
  );
}
